import { noop, map as mapSpecificKey, isEqual } from 'lodash'
import { useState, useRef, useEffect } from 'react'
import MultiSelectSearchDropDown from '../../select-dropdown/MultiSelectSearchDropDown'
import { DropdownFilterInputPropsType } from '../../../utils/types/tableFilterTypes'
import {
  DropdownType,
  filterOperatorValues,
  tableFilterType,
} from '../../../utils'

export const DropdownFilterInput = <T,>({
  columnData,
  handleFilterChange = noop,
  dataQaId,
  filterOperator,
  filterObject,
  columnHeaderRef,
  innerInputRef,
  tabIndex,
}: DropdownFilterInputPropsType<T>) => {
  const search_key =
    columnData?.filterData?.convertToUppercase === false
      ? (columnData?.accessor as string)
      : (columnData?.accessor as string)?.toUpperCase()
  const [dropdownOptions, setDropdownOptions] = useState<DropdownType<T>[]>(
    columnData.filterData?.dropDownOptions ?? []
  )
  const [disabledInput, setDisabledInput] = useState(false)
  const [showClearFilter, setShowClearFilter] = useState<boolean>(false)
  const [selectedOptions, setSelectedOptions] = useState<
    DropdownType<T>[] | undefined
  >(undefined)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const getFilterOperator = () => {
    if (
      columnData.filterData?.dataType === tableFilterType.MULTI_SELECT_DYNAMIC
    )
      return filterOperatorValues.CONTAINS
    else if (columnData.filterData?.isMultiSelect)
      return filterOperatorValues.CONTAINS_LIST
    return filterOperatorValues.IN
  }

  const onFocusOut = (e: DropdownType<T>[]) => {
    if (
      !isEqual(
        mapSpecificKey(e, 'value'),
        filterObject?.search_criteria?.[search_key]?.search_value
      )
    )
      handleFilterChange({
        search_key: search_key,
        operator: getFilterOperator(),
        search_value: mapSpecificKey(e, 'value'),
      })
    setDropdownOptions(columnData.filterData?.dropDownOptions ?? [])
  }
  const isNullOrNotNull = () => {
    return (
      filterOperator?.value === filterOperatorValues.IS_NULL ||
      filterOperator?.value === filterOperatorValues.IS_NOT_NULL
    )
  }

  const getMultiSelectDropdownOptions = (searchTerm: string) => {
    const data = columnData.filterData?.dropDownOptions?.filter((item) =>
      item.label.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
    )
    setDropdownOptions(data ?? [])
  }

  useEffect(() => {
    innerInputRef.current = inputRef.current
  }, [])

  useEffect(() => {
    if (isNullOrNotNull()) {
      setSelectedOptions([])
      handleFilterChange({
        search_key: search_key,
        operator: filterOperator?.value ?? '',
        search_value: [''],
      })
      setDisabledInput(true)
      setShowClearFilter(true)
    } else {
      setDisabledInput(false)
      setShowClearFilter(false)
    }
  }, [filterOperator])

  useEffect(() => {
    innerInputRef.current = inputRef.current
  }, [])

  useEffect(() => {
    if (isNullOrNotNull()) {
      return
    }
    const rawValues =
      (filterObject?.search_criteria?.[search_key]?.search_value as
        | unknown[]
        | undefined) ?? []

    if (!rawValues?.length) {
      setSelectedOptions([])
      setShowClearFilter(false)
      return
    }

    const mapped: DropdownType<T>[] = rawValues.map((v) => {
      const match =
        dropdownOptions.find((opt) => String(opt.value) === String(v)) ?? null
      if (match) return match
      return {
        label: v as string,
        value: v as string,
      } as DropdownType<T>
    })
    setSelectedOptions(mapped)
    setShowClearFilter(true)
  }, [filterObject])

  return (
    <div className="flex-1">
      <MultiSelectSearchDropDown
        options={dropdownOptions}
        name={columnData.accessor as string}
        label=""
        required={false}
        onSearch={getMultiSelectDropdownOptions}
        onFocusOut={onFocusOut}
        defaultSelected={selectedOptions}
        inpref={inputRef}
        dataQaId={`${dataQaId}_dropdown_1`}
        allowCustomValues={false}
        disabled={disabledInput}
        showCrossIcon={showClearFilter}
        excludedParentRef={columnHeaderRef}
        isFilterApplied={
          (filterObject?.search_criteria?.[search_key]?.search_value?.length ??
            0) > 0 ||
          filterOperator?.value === filterOperatorValues.IS_EMPTY_NULL ||
          filterOperator?.value === filterOperatorValues.IS_NOT_EMPTY_NOT_NULL
        }
        isFromFilter={true}
        tabIndex={tabIndex}
      />
    </div>
  )
}
